<div class="max-w-4xl mx-auto p-8 min-h-screen">
    <p-toast></p-toast>

    <!-- Header Section -->
    <div class="text-start mb-8">
        <p class="font-bold text-2xl mb-1" *ngIf="!isFromCustomerLookup" i18n>Create New Reservation</p>
        <p class="font-bold text-2xl mb-1" *ngIf="isFromCustomerLookup" i18n>Confirm Reservation</p>
        <p class="text-[#6B7280] text-sm m-0" *ngIf="!isFromCustomerLookup" i18n>Reserve this client for a specific product or products for a specific period</p>
        <p class="text-[#6B7280] text-sm m-0" *ngIf="isFromCustomerLookup" i18n>Review and confirm the products for this reservation. You can deselect products if needed.</p>
    </div>

    <div class="space-y-6">
        <form [formGroup]="reservationForm" class="space-y-8">
            <p-card styleClass="p-4">
                <!-- Simple loader inside card -->
                <div *ngIf="isFormLoading" class="text-center py-8">
                    <p-progressSpinner styleClass="w-6 h-6"></p-progressSpinner>
                    <p class="text-sm text-gray-600 mt-2" i18n>Loading...</p>
                </div>
                
                <!-- Form fields - only show when not loading -->
                <div *ngIf="!isFormLoading">
                    <!-- Customer Information (Confirmation Mode) -->
                    <div *ngIf="isFromCustomerLookup" class="mb-6">
                        <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <div class="flex items-center gap-3">
                                <i class="pi pi-building text-2xl text-gray-600"></i>
                                <div>
                                    <p class="font-semibold text-gray-900 m-0 text-lg" *ngIf="customerName">{{ customerName }}</p>
                                    <p class="text-sm text-gray-600 m-0" i18n>Customer</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Customer Selection (Normal Mode) -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5" *ngIf="!isFromCustomerLookup">
                        <div class="flex flex-col gap-2">
                            <label for="customerId" class="font-medium text-sm text-[#374151] mb-1" i18n>
                                Customer <span class="text-red-500">*</span>
                            </label>
                            <p-select 
                                id="customerId"
                                formControlName="customerId"
                                [options]="customerOptions"
                                optionLabel="label"
                                optionValue="value"
                                i18n-placeholder
                                placeholder="Select customer"
                                class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100">
                            </p-select>
                        </div>
                    </div>
                    
                    <!-- Products Section -->
                    <div class="mb-6 relative">
                        <!-- Loading Overlay -->
                        <div *ngIf="loading" class="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50 rounded-lg">
                            <div class="text-center">
                                <p-progressSpinner styleClass="w-8 h-8"></p-progressSpinner>
                                <p class="text-sm text-gray-600 mt-3 font-medium" i18n>Creating reservation...</p>
                            </div>
                        </div>
                        
                        <!-- Confirmation Mode - Show available products with checkboxes -->
                        <div *ngIf="isFromCustomerLookup" class="space-y-4">
                            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div class="flex items-start gap-3">
                                    <i class="pi pi-info-circle text-blue-500 text-xl mt-0.5"></i>
                                    <div>
                                        <p class="font-semibold text-blue-900 m-0 mb-2" i18n>Review Products</p>
                                        <p class="text-blue-800 text-sm m-0" i18n>
                                            You are about to create reservations for the following products. 
                                            Please review and select/deselect products as needed.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="space-y-3">
                                <label class="font-medium text-sm text-[#374151] mb-3 block" i18n>
                                    Select Products for Reservation <span class="text-red-500">*</span>
                                </label>
                                <div class="border border-gray-200 rounded-lg divide-y divide-gray-200">
                                    <div *ngFor="let product of availableProducts" 
                                         class="p-4 hover:bg-gray-50 transition-colors"
                                         [class.opacity-60]="loading">
                                        <div class="flex items-center gap-3">
                                            <p-checkbox 
                                                [binary]="true" 
                                                [ngModel]="isProductSelected(product)"
                                                [ngModelOptions]="{standalone: true}"
                                                [disabled]="loading"
                                                (ngModelChange)="onProductCheckboxChange(product, $event)">
                                            </p-checkbox>
                                            <div class="flex-1 cursor-pointer" (click)="!loading && toggleProductSelection(product)">
                                                <p class="font-medium text-gray-900 m-0">{{ product.label }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div *ngIf="selectedProducts.length === 0 && !loading" class="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                                    <i class="pi pi-exclamation-triangle mr-2"></i>
                                    <span i18n>Please select at least one product to continue.</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Normal Mode - Product selector with Add button -->
                        <div *ngIf="!isFromCustomerLookup" class="space-y-4">
                            <label for="products" class="font-medium text-sm text-[#374151] mb-1" i18n>
                                Products <span class="text-red-500">*</span>
                            </label>
                            <div class="card flex justify-center gap-3">
                                <p-select 
                                    [ngModel]="selectedProduct"
                                    [ngModelOptions]="{standalone: true}"
                                    [options]="productsOptions"
                                    optionLabel="label"
                                    [filter]="true"
                                    filterBy="label"
                                    [showClear]="true"
                                    [disabled]="loading"
                                    i18n-placeholder
                                    placeholder="Select a Product"
                                    (onChange)="onProductChange($event)"
                                    class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100">
                                </p-select>
                                <p-button 
                                    i18n-label
                                    label="Add" 
                                    severity="secondary" 
                                    [disabled]="!selectedProduct || loading"
                                    (click)="addSelectedProduct()">
                                </p-button>
                            </div>
                            <div class="flex flex-wrap gap-2 industry-region-tags" [class.opacity-60]="loading">
                                <div *ngFor="let product of selectedProducts" class="tag">
                                    <span class="tag-text">{{ product.label }}</span>
                                    <button class="remove-button" 
                                            [disabled]="loading"
                                            (click)="removeProduct(product)" 
                                            i18n-aria-label 
                                            aria-label="Remove product">×</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="flex justify-start pt-6 gap-3 flex-wrap">
                        <p-button 
                            type="button" 
                            i18n-label
                            label="Create Reservation" 
                            styleClass="p-button-primary"
                            [loading]="loading"
                            [disabled]="reservationForm.invalid || loading"
                            (onClick)="createReservation()"
                            fluid="true"
                        >
                        </p-button>
                        <p-button 
                            type="button" 
                            i18n-label
                            label="Cancel" 
                            [disabled]="reservationForm.invalid || loading"
                            styleClass="p-button-outlined p-button-secondary"
                            (onClick)="customerSinglePage()"
                            fluid="true"
                        >
                        </p-button>
                    </div>
                </div> <!-- End of form fields conditional div -->

            </p-card>



        </form>
    </div>


</div>